import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { apiUrl, fetchJson } from '../../apiBase';
import { sendWelcomeTemplate } from '../api/whatsapp';

const api = {
  parents: (school) => apiUrl(`/admin/parents/${encodeURIComponent(school)}`),
  students: (school) => apiUrl(`/admin/students/${encodeURIComponent(school)}`),
  classes: (school) => apiUrl(`/admin/classes/${encodeURIComponent(school)}`),
  optIns: (school) => apiUrl(`/admin/opt-in/${encodeURIComponent(school)}`),
};

export default function ParentOptInManager({ schoolId, onError, onSuccess, readOnly = false }) {
  const school = schoolId || '';
  const [parents, setParents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [onlyNoOptIn, setOnlyNoOptIn] = useState(false);
  const [selectedClassName, setSelectedClassName] = useState('');
  const [welcomeParent, setWelcomeParent] = useState(null);
  const [snack, setSnack] = useState('');
  const [sendingId, setSendingId] = useState(null);

  const token = useMemo(() => JSON.parse(localStorage.getItem('adminUser') || '{}')?.token, []);
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const load = useCallback(async () => {
    if (!school) return;
    setLoading(true);
    try {
      const [parentsRes, studentsRes, classesRes, optInsRes] = await Promise.all([
        fetchJson(api.parents(school), { headers: authHeaders }),
        fetchJson(api.students(school), { headers: authHeaders }),
        fetchJson(api.classes(school), { headers: authHeaders }),
        fetchJson(api.optIns(school), { headers: authHeaders }),
      ]);

      if (!parentsRes.ok) throw new Error('Veli listesi yüklenemedi');
      const parentsJson = await parentsRes.json();
      const parentList = Array.isArray(parentsJson) ? parentsJson : [];

      const studentsById = new Map();
      if (studentsRes.ok) {
        const studentsJson = await studentsRes.json();
        const students = Array.isArray(studentsJson) ? studentsJson : [];
        students.forEach((st) => {
          const sid = st?.id;
          if (sid === undefined || sid === null) return;
          studentsById.set(String(sid), st);
        });
      }

      if (classesRes.ok) {
        const classesJson = await classesRes.json();
        const items = Array.isArray(classesJson) ? classesJson : [];
        const normalized = items
          .map((c) => {
            const name = String(c?.name || c?.code || '').trim();
            return name ? { name } : null;
          })
          .filter(Boolean);
        // Sort by natural-ish order; simple locale sort is fine for now.
        normalized.sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr-TR'));
        setClasses(normalized);
      } else {
        setClasses([]);
      }

      const optInByParentId = new Map();
      if (optInsRes.ok) {
        const optInsJson = await optInsRes.json();
        const items = Array.isArray(optInsJson?.items) ? optInsJson.items : [];
        items.forEach((it) => {
          const parentId = it?.parent_id;
          if (parentId === undefined || parentId === null) return;
          optInByParentId.set(String(parentId), it);
        });
      }

      const merged = parentList.map((p) => {
        const foundOptIn = p?.id === undefined || p?.id === null ? null : optInByParentId.get(String(p.id));

        const studentIds = Array.isArray(p?.student_ids) ? p.student_ids : [];
        const students = studentIds
          .map((sid) => studentsById.get(String(sid)))
          .filter(Boolean);

        return {
          ...p,
          whatsapp_opt_in_at: foundOptIn?.opt_in_date || '',
          students,
        };
      });

      setParents(merged);
    } catch (e) {
      onError && onError(e.message || 'Veli listesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, onError, school]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredParents = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    let out = parents;
    if (q) {
      out = out.filter((p) => {
        const fullName = `${p.name || ''} ${p.surname || ''}`.trim().toLowerCase();
        const studentText = Array.isArray(p.students)
          ? p.students
              .map((st) => `${st?.name || ''} ${st?.surname || ''} ${st?.class_name || ''}`.trim())
              .join(' ')
              .toLowerCase()
          : '';

        return fullName.includes(q) || studentText.includes(q);
      });
    }

    if (onlyNoOptIn) {
      out = out.filter((p) => !Boolean(p.whatsapp_opt_in_at));
    }

    if (selectedClassName) {
      if (selectedClassName === '__unknown__') {
        out = out.filter((p) => !(Array.isArray(p.students) && p.students.length));
      } else {
        out = out.filter((p) =>
          Array.isArray(p.students)
            ? p.students.some((st) => String(st?.class_name || '').trim() === selectedClassName)
            : false
        );
      }
    }

    return out;
  }, [onlyNoOptIn, parents, query, selectedClassName]);

  const confirmWelcome = async () => {
    if (!welcomeParent) return;
    setSendingId(welcomeParent.id);
    try {
      await sendWelcomeTemplate(school, String(welcomeParent.id));
      setSnack('Karşılama mesajı kuyruğa eklendi');
      onSuccess && onSuccess('Karşılama mesajı kuyruğa eklendi');
    } catch (e) {
      const msg = e?.message || 'Karşılama mesajı gönderilemedi';
      setSnack(msg);
      onError && onError(msg);
    } finally {
      setSendingId(null);
      setWelcomeParent(null);
    }
  };

  const activeCount = parents.filter((p) => Boolean(p.whatsapp_opt_in_at)).length;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Veli ara (ad/telefon/öğrenci)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ minWidth: 0, flex: '1 1 260px', '& .MuiInputBase-root': { minHeight: 44 } }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="parent-opt-in-class-label">Sınıf</InputLabel>
            <Select
              labelId="parent-opt-in-class-label"
              label="Sınıf"
              sx={{ '&& .MuiSelect-select': { minHeight: 44, boxSizing: 'border-box', display: 'flex', alignItems: 'center' } }}
              value={selectedClassName}
              onChange={(e) => setSelectedClassName(String(e.target.value || ''))}
            >
              <MenuItem value="">Tümü</MenuItem>
              {classes.map((c) => (
                <MenuItem key={c.name} value={c.name}>
                  {c.name}
                </MenuItem>
              ))}
              <MenuItem value="__unknown__">Bilinmeyen</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" sx={{ minHeight: 44 }} onClick={load} disabled={loading}>
            {loading ? (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Yükleniyor…
              </Box>
            ) : (
              'Yenile'
            )}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Chip size="small" label={`Toplam: ${parents.length}`} />
          <Chip size="small" color="success" label={`Opt-in: ${activeCount}`} />
          <Chip size="small" variant="outlined" label={`Opt-in yok: ${Math.max(parents.length - activeCount, 0)}`} />
          <Chip
            size="small"
            clickable
            aria-pressed={onlyNoOptIn}
            sx={{ minHeight: 44 }}
            color={onlyNoOptIn ? 'primary' : 'default'}
            variant={onlyNoOptIn ? 'filled' : 'outlined'}
            label="Sadece opt-in yok"
            onClick={() => setOnlyNoOptIn((v) => !v)}
          />
        </Box>
      </Paper>

      <Paper
        role="region"
        aria-label="Veli kayıt listesi"
        tabIndex={0}
        sx={{
          p: 1.5,
          borderRadius: 2,
          maxHeight: 'min(56dvh, 640px)',
          overflowY: 'auto',
          '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: -3 },
        }}
      >
        <List dense>
          {filteredParents.map((p) => {
            const fullName = `${p.name || ''} ${p.surname || ''}`.trim() || 'Adsız Veli';
            const hasOptIn = Boolean(p.whatsapp_opt_in_at);
            return (
              <ListItem
                key={p.id}
                disableGutters
                sx={{ px: 1, py: 1.25, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {fullName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Array.isArray(p.students) && p.students.length
                        ? p.students
                            .map((st) => {
                              const studentName = `${st?.name || ''} ${st?.surname || ''}`.trim();
                              const className = String(st?.class_name || '').trim();
                              if (studentName && className) return `${studentName} (${className})`;
                              return studentName || className || '';
                            })
                            .filter(Boolean)
                            .join(', ')
                        : 'Bilinmeyen'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
                    {hasOptIn ? (
                      <Tooltip
                        title={`Onay: ${new Date(p.whatsapp_opt_in_at).toLocaleString('tr-TR')}`}
                      >
                        <Chip size="small" color="success" label="WhatsApp aktif" />
                      </Tooltip>
                    ) : (
                      <>
                        <Chip size="small" variant="outlined" label="WhatsApp onayı yok" />
                        {!readOnly && p.phone ? (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ minHeight: 44 }}
                            disabled={sendingId === p.id}
                            onClick={() => setWelcomeParent(p)}
                          >
                            Karşılama Mesajı
                          </Button>
                        ) : null}
                      </>
                    )}
                  </Box>
                </Box>
              </ListItem>
            );
          })}
          {!loading && filteredParents.length === 0 ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Veli bulunamadı.
              </Typography>
            </Box>
          ) : null}
        </List>
      </Paper>

      <Dialog open={Boolean(welcomeParent)} onClose={() => setWelcomeParent(null)}>
        <DialogTitle>WhatsApp Karşılama Mesajı Gönderilsin mi?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Merhaba 👋
            <br />
            Çocuklarınızın yoklama, sınav ve duyuru bilgilerini WhatsApp üzerinden alabilmeniz için buradayım.
            <br />
            <br />
            Kaydınızı tamamlamak için bu mesaja yanıt vermeniz yeterli ✅
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWelcomeParent(null)} disabled={Boolean(sendingId)}>Vazgeç</Button>
          <Button variant="contained" onClick={confirmWelcome} disabled={Boolean(sendingId)}>
            Gönder
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(snack)} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
